/**
 * Record a voice note to explain a feature request, then upload it in chunks. Voice only, on
 * purpose: the transcript carries the substance, and audio is ~20x lighter than video.
 */
import { useEffect, useRef, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Mic, Square, Loader2, Trash2, Check } from "lucide-react";

export const MAX_SECONDS = 180;

const pickMime = (cands) => cands.find((m) => window.MediaRecorder?.isTypeSupported?.(m)) || "";
const fmtSecs = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

export async function uploadRecording(tenantId, blob, durationS, onPct) {
  const start = await api.post(`/tenants/${tenantId}/copilot/product-signals/recordings/start`, {
    kind: "voice", size: blob.size, mime: blob.type || "audio/webm", duration_s: durationS,
  });
  const { recording_id: rid, chunk_bytes: cb } = start.data;
  const total = Math.ceil(blob.size / cb);
  for (let i = 0; i < total; i++) {
    const fd = new FormData();
    fd.append("index", String(i));
    fd.append("file", blob.slice(i * cb, (i + 1) * cb), `part-${i}`);
    await api.post(`/tenants/${tenantId}/copilot/product-signals/recordings/${rid}/chunk`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    onPct?.(Math.round(((i + 1) / total) * 90));
  }
  const r = await api.post(`/tenants/${tenantId}/copilot/product-signals/recordings/${rid}/finish`);
  onPct?.(100);
  return r.data;
}

export function SignalRecorder({ tenantId, value, onChange, disabled }) {
  const [phase, setPhase] = useState("idle"); // idle | recording | uploading
  const [seconds, setSeconds] = useState(0);
  const [pct, setPct] = useState(0);
  const [previewUrl, setPreviewUrl] = useState("");
  const recRef = useRef(null);
  const streamsRef = useRef([]);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const startedRef = useRef(0);

  const stopStreams = () => {
    streamsRef.current.forEach((s) => s.getTracks().forEach((t) => t.stop()));
    streamsRef.current = [];
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  };
  useEffect(() => () => { stopStreams(); if (previewUrl) URL.revokeObjectURL(previewUrl); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const finish = async () => {
    stopStreams();
    const mime = recRef.current?.mimeType || "audio/webm";
    const blob = new Blob(chunksRef.current, { type: mime.split(";")[0] });
    const dur = Math.max(1, Math.round((Date.now() - startedRef.current) / 1000));
    if (blob.size < 1000) { setPhase("idle"); toast.error("That recording was empty — try again."); return; }
    const url = URL.createObjectURL(blob);
    setPreviewUrl(url);
    setPhase("uploading");
    setPct(0);
    try {
      const rec = await uploadRecording(tenantId, blob, dur, setPct);
      onChange({ ...rec, previewUrl: url });
      setPhase("idle");
    } catch (e) {
      setPhase("idle");
      toast.error(errMsg(e, "Couldn't upload the recording"));
    }
  };

  const begin = async () => {
    chunksRef.current = [];
    try {
      const mic = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamsRef.current.push(mic);
      const rec = new MediaRecorder(mic, { mimeType: pickMime(["audio/webm;codecs=opus", "audio/webm", "audio/mp4"]) || undefined, audioBitsPerSecond: 64_000 });
      rec.ondataavailable = (e) => { if (e.data?.size) chunksRef.current.push(e.data); };
      rec.onstop = () => finish();
      rec.start(1000);
      recRef.current = rec;
      setSeconds(0); setPhase("recording");
      startedRef.current = Date.now();
      timerRef.current = setInterval(() => setSeconds((s) => {
        if (s + 1 >= MAX_SECONDS) { stopRec(); return MAX_SECONDS; }
        return s + 1;
      }), 1000);
    } catch (e) {
      stopStreams();
      toast.error(e?.name === "NotAllowedError" ? "Microphone permission was declined — allow it to record a note." : "Couldn't start recording on this device.");
    }
  };

  const stopRec = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    const r = recRef.current;
    if (r && r.state !== "inactive") r.stop();
  };

  const clear = () => { if (previewUrl) URL.revokeObjectURL(previewUrl); setPreviewUrl(""); onChange(null); };

  if (value) {
    return (
      <div className="rounded-xl border border-sand-200 bg-white p-2.5 space-y-2" data-testid="signal-recording-ready">
        <div className="flex items-center gap-2 text-xs text-ink/70">
          <Check className="h-3.5 w-3.5 text-emerald-600" strokeWidth={2.5} />
          <span className="font-semibold">Voice note</span>
          {value.duration_s ? <span>· {fmtSecs(value.duration_s)}</span> : null}
          {!disabled && (
            <button type="button" onClick={clear} data-testid="signal-recording-remove" aria-label="Remove recording"
              className="ml-auto inline-flex h-8 w-8 items-center justify-center rounded-full text-ink/40 hover:bg-rose-50 hover:text-rose-600">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        {value.previewUrl && <audio src={value.previewUrl} controls className="w-full h-9" data-testid="signal-recording-preview" />}
        {value.transcript ? (
          <p className="text-[11px] text-ink/55 italic line-clamp-3" data-testid="signal-recording-transcript">“{value.transcript}”</p>
        ) : (
          <p className="text-[11px] text-ink/45">No transcript — the team will listen to it.</p>
        )}
      </div>
    );
  }

  if (phase === "uploading") {
    return (
      <div className="flex items-center gap-2 text-xs text-ink/60" data-testid="signal-recording-uploading">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Uploading your voice note… {pct}%
      </div>
    );
  }

  if (phase === "recording") {
    return (
      <div className="flex flex-wrap items-center gap-2" data-testid="signal-recording-live">
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-rose-600">
          <span className="h-2 w-2 rounded-full bg-rose-500 animate-pulse" /> Recording · {fmtSecs(seconds)} / {fmtSecs(MAX_SECONDS)}
        </span>
        <button type="button" onClick={stopRec} data-testid="signal-recording-stop"
          className="inline-flex h-9 items-center gap-1.5 rounded-full bg-ink px-3.5 text-xs font-semibold text-white hover:bg-ink-700">
          <Square className="h-3 w-3" strokeWidth={2.5} /> Stop
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="signal-recording-idle">
      <button type="button" onClick={begin} disabled={disabled} data-testid="signal-record-voice"
        className="inline-flex h-9 items-center gap-1.5 rounded-full border border-sand-200 bg-white px-3.5 text-xs font-semibold text-ink/80 hover:border-ink disabled:opacity-50">
        <Mic className="h-3.5 w-3.5" strokeWidth={2.25} /> Record a voice note
      </button>
      <span className="text-[11px] text-ink/45">Optional · up to 3 minutes · say it in your own words</span>
    </div>
  );
}
